import { Client } from './client'
import { Contact } from './contact'
import { Demand } from './demand'
import { Department } from './department'
import { Message } from './message'
import { User } from './user'
import type { ConversationRoutingArea } from '@/shared/constants/status'

export type RoutingState = 'pending' | 'selected' | 'expired' | 'cancelled'

export interface RoutingSelectedDto {
  departmentId: number
  by?: string
  at?: string
  userId?: number
}

export interface RoutingMetadata {
  state: RoutingState
  options?: Record<string, number>
  labels?: Record<string, string>
  tries?: number
  maxTries?: number
  initiatedAt?: string
  expiresAt?: string
  selected?: RoutingSelectedDto | null
}

export interface ConversationWindowMetadata {
  lastCustomerMessageAt?: string
  expiresAt?: string
  canSendFreeForm?: boolean
  requiresTemplate?: boolean
}

export interface ConversationMessagingPolicy {
  directTransport: 'official' | 'unofficial'
  canUseTemplates: boolean
  enforcesConversationWindow: boolean
  canSendFreeForm: boolean
  requiresTemplate: boolean
}

export interface ConversationMetadata {
  routing?: RoutingMetadata
  conversationWindow?: ConversationWindowMetadata
  messagingPolicy?: ConversationMessagingPolicy
  waitingQueue?: {
    enteredAt: string
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export interface Conversation {
  id: number
  routingArea?: ConversationRoutingArea
  opportunityId?: number | null
  status: string
  active: boolean
  score: number
  unreadCount: number
  isGroup: boolean
  client: Client
  clientId: number
  contact: Contact
  user?: User
  userId: number
  secondaryUser?: User
  secondaryUserId: number
  departmentId: number
  department?: Department | null
  messages: Message[]
  startTime: string
  finishTime?: string | null
  demands?: Demand[]
  demandOccurrences?: ConversationDemandOccurrence[]
  createdAt: string
  updatedAt: string
  metadata?: ConversationMetadata
}

export interface ConversationDemandOccurrence {
  conversationId: number
  demandId: number
  demand: Demand
  extraServiceValue: number | null
  registeredAt: string | null
  registeredByUserId: number | null
  registeredByUser?: User | null
}

type ConversationActivityDateInput = Pick<Conversation, 'createdAt'> & {
  messages?: Message[] | null
  finishTime?: string | null
}

function getDateTimestamp(date?: string | null) {
  if (!date) return 0

  const timestamp = new Date(date).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function getConversationActivityDate(
  conversation: ConversationActivityDateInput
) {
  const latestMessage = (conversation.messages ?? []).reduce<
    Message | undefined
  >((latestMessage, message) => {
    const currentTimestamp = getDateTimestamp(
      message.sentAt ?? message.createdAt
    )
    const latestTimestamp = getDateTimestamp(
      latestMessage?.sentAt ?? latestMessage?.createdAt
    )

    return currentTimestamp > latestTimestamp ? message : latestMessage
  }, undefined)

  return (
    conversation.finishTime ||
    latestMessage?.sentAt ||
    latestMessage?.createdAt ||
    conversation.createdAt
  )
}

export function getConversationActivityTimestamp(
  conversation: ConversationActivityDateInput
) {
  return getDateTimestamp(getConversationActivityDate(conversation))
}

export function getLatestConversationDemand(
  conversation: Pick<Conversation, 'demands'>
) {
  if (!conversation.demands?.length) return undefined

  return conversation.demands[conversation.demands.length - 1]
}

export function getConversationDemands(
  conversation: Pick<Conversation, 'demands'>
) {
  return conversation.demands ?? []
}

export interface GroupedConversation {
  waiting: Conversation[]
  inService: Conversation[]
  finished: Conversation[]
}

export interface ConversationSnapshotPage {
  items: Conversation[]
  nextCursor: string | null
  hasMore: boolean
  syncCursor: string | null
}

export interface ConversationSyncCursor {
  companyId: number
  userId: number
  value: string
}

export interface ConversationChangeAvailable {
  sequence: string
  contractVersion: number
}

export interface ConversationSyncPage {
  upserts: Conversation[]
  removedIds: number[]
  cursor: string
  hasMore: boolean
  resetRequired: boolean
}

export interface DashboardConversation {
  id: number
  routingArea?: ConversationRoutingArea
  opportunityId?: number | null
  status: string
  companyName: string
  internalCode?: string | null
  contactName: string
  userName: string
  secondaryUserName: string
  score: number
}

export interface DashboardConversationByScore {
  promoters: DashboardConversation[]
  neutrals: DashboardConversation[]
  detractors: DashboardConversation[]
}

export interface GroupedDashboardConversationList {
  waiting: DashboardConversation[]
  inService: DashboardConversation[]
  finished: DashboardConversation[]
}
